import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';

import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import type { VariableIdType } from '../../types';
import { removeCatalogOtherCostVariableGql } from '../catalogRemoveOtherVariableQueries';

export const useRemoveOtherVariable = ({ updateCatalogVariables }: { updateCatalogVariables: () => void }) => {
    const dispatch = useDispatch();

    const [removingVariable, setRemovingVariable] = useState<Exclude<VariableIdType, undefined>>(null);

    const [resetRemoveData] = useResetStoreData({ requestId: 'removeCatalogOtherCostVariable' });

    const tempSuccessHandler = useCallback(() => {
        updateCatalogVariables();
        resetRemoveData(undefined);
    }, [resetRemoveData, updateCatalogVariables]);

    const [, , , removeOtherVariableRequest] = useRequest({
        requestId: 'removeCatalogOtherCostVariable',
        query: removeCatalogOtherCostVariableGql,
        withEffect: false,
        dependenciesUpdater: tempSuccessHandler,
    });

    const removeOtherVariableHandler = useCallback(() => {
        dispatch(removeOtherVariableRequest({ otherCostVariableId: removingVariable }));
    }, [dispatch, removeOtherVariableRequest, removingVariable]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: removeOtherVariableHandler,
        });

    const handleOpenRemoveDialog = useCallback(
        (variableId: Exclude<VariableIdType, undefined>) => () => {
            setRemovingVariable(variableId);
            handleOpenConfirmationDialog();
        },
        [handleOpenConfirmationDialog],
    );

    return useMemo(
        () => ({
            handleDelete: handleActionQuery,
            handleOpenRemoveDialog,
            isOpenConfirmationDialog,
            handleCloseConfirmationDialog,
        }),
        [handleActionQuery, handleCloseConfirmationDialog, handleOpenRemoveDialog, isOpenConfirmationDialog],
    );
};
